"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRightFill } from "react-icons/pi";

interface BlogItem {
  title: string;
  date: string;
  image: string;
  url: string;
}

export default function OvanBlog({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  const defaultItems: BlogItem[] = [
    {
      title: "Design is not just what it looks like and feels like.",
      date: "23rd Nov 2024",
      image: "/assets/1-CQhRN4X-.jpg",
      url: "/blog-standard-post",
    },
    {
      title: "Modern Branding Campaign and Digital Identity Development.",
      date: "10th Dec 2024",
      image: "/assets/2-DbpuiLLT.jpg",
      url: "/blog-standard-post",
    },
    {
      title: "Simplicity is the ultimate sophistication of web layouts.",
      date: "15th Jan 2025",
      image: "/assets/3-KgvXx2Fx.jpg",
      url: "/blog-standard-post",
    },
  ];

  const items: BlogItem[] = content.items || defaultItems;

  const updateItem = (idx: number, field: keyof BlogItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  return (
    <section
      className={`py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${
        isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center pb-12 max-w-xl mx-auto">
          <h2 className={`mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            <EditableText
              value={content.title || "Latest Updates on Instacks News"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, title: val })
              }
              as="span"
              placeholder="Blog Section Title"
            />
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            <EditableText
              value={
                content.text ||
                "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
              }
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, text: val })
              }
              as="span"
              placeholder="Blog Section Subtitle"
            />
          </p>
        </div>

        {/* Grid */}
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`group rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 border ${
                isDark
                  ? "bg-slate-800/40 border-slate-800/40 hover:bg-slate-800/60"
                  : "bg-slate-50 border-slate-100/50 hover:bg-white"
              }`}
            >
              {/* Image Frame */}
              <div className="relative overflow-hidden h-52">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
                />
              </div>

              {/* Text Body */}
              <div className="p-5">
                <span className="text-slate-400 text-xs font-semibold block mb-2">
                  <EditableText
                    value={item.date}
                    onChange={(val) => updateItem(idx, "date", val)}
                    as="span"
                    placeholder="Blog Date"
                  />
                </span>
                <div className={`text-md font-semibold mb-4 leading-normal transition-colors group-hover:text-blue-600 line-clamp-2 h-12 ${
                  isDark ? "text-white" : "text-slate-900"
                }`}>
                  <EditableText
                    value={item.title}
                    onChange={(val) => updateItem(idx, "title", val)}
                    as="span"
                    placeholder="Blog Title"
                  />
                </div>
                <a
                  href={item.url}
                  className="inline-flex items-center text-xs uppercase font-bold tracking-wider text-blue-600 hover:text-blue-700"
                >
                  Read More <PiArrowRightFill className="ms-1 size-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
